import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { 
  Loader2, AlertTriangle, CheckCircle, Search, User, Calendar, 
  Ticket, DollarSign, ArrowRight, Info, MapPin, Plus, Minus, Copy, X, Bug
} from "lucide-react";

const AdminTransferForm = ({ onSuccess, onCancel, token }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [transferFee, setTransferFee] = useState(24.95);
  const [feeType, setFeeType] = useState('fixed');
  const [platforms, setPlatforms] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Client lookup states
  const [sellerClientNumber, setSellerClientNumber] = useState("");
  const [buyerClientNumber, setBuyerClientNumber] = useState("");
  const [sellerInfo, setSellerInfo] = useState(null);
  const [buyerInfo, setBuyerInfo] = useState(null);
  const [sellerSearching, setSellerSearching] = useState(false);
  const [buyerSearching, setBuyerSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    creator_role: "seller",
    event_name: "",
    event_date: "",
    event_time: "",
    venue: "",
    ticket_count: 1,
    price: "",
    currency: "CAD",
    ticket_platform: "Ticketmaster",
    event_type: "concert",
    seat_type: "",
    special_section_details: "",
    fee_payer: "buyer",
    admin_notes: ""
  });

  // Per-ticket details for "siege_assigne"
  const [ticketDetails, setTicketDetails] = useState([{ level: "", section: "", row: "", seat: "" }]);

  // Fetch initial data on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch transfer fee settings
        const feeResponse = await axios.get(`${API}/transfer-fee`);
        if (feeResponse.data) {
          setFeeType(feeResponse.data.fee_type || 'fixed');
          if (feeResponse.data.fee_type === 'percentage') {
            setTransferFee(feeResponse.data.fee_value || 5);
          } else {
            setTransferFee(feeResponse.data.transfer_fee || 24.95);
          }
        }
        
        // Fetch platforms from API
        const platformsResponse = await axios.get(`${API}/platforms`);
        if (platformsResponse.data && platformsResponse.data.length > 0) {
          setPlatforms(platformsResponse.data);
          setFormData(prev => ({
            ...prev,
            ticket_platform: platformsResponse.data[0].name
          }));
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();
  }, []);

  // Update ticket details count when ticket_count changes
  useEffect(() => {
    const count = parseInt(formData.ticket_count) || 1;
    if (formData.seat_type === 'siege_assigne') {
      setTicketDetails(prev => {
        if (prev.length < count) {
          return [...prev, ...Array(count - prev.length).fill({ level: "", section: "", row: "", seat: "" })];
        } else if (prev.length > count) {
          return prev.slice(0, count);
        }
        return prev;
      });
    }
  }, [formData.ticket_count, formData.seat_type]);

  // Search for client by number
  const searchClient = async (clientNumber, type) => {
    if (!clientNumber || clientNumber.length < 1) {
      if (type === 'seller') {
        setSellerInfo(null);
      } else {
        setBuyerInfo(null);
      }
      return;
    }

    const setSearching = type === 'seller' ? setSellerSearching : setBuyerSearching;
    const setInfo = type === 'seller' ? setSellerInfo : setBuyerInfo;

    setSearching(true);
    try {
      const response = await axios.get(`${API}/clients/lookup/${clientNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInfo(response.data);
    } catch (error) {
      setInfo(null);
      if (error.response?.status !== 404) {
        toast.error("Erreur lors de la recherche");
      }
    } finally {
      setSearching(false);
    }
  };

  // Fee calculation constants - tiered pricing based on TOTAL transfer amount
  const FEE_TIER_LOW = 6.95;      // For transfers $125 or less
  const FEE_TIER_MID = 9.95;      // For transfers $125.01 to $250
  const FEE_TIER_HIGH = 19.95;    // For transfers over $250
  const FEE_THRESHOLD_LOW = 125.00;   // Low tier threshold
  const FEE_THRESHOLD_HIGH = 250.00;  // High tier threshold

  // Calculate the fee based on TOTAL transfer amount
  const getFeeForPrice = (totalPrice) => {
    if (totalPrice <= FEE_THRESHOLD_LOW) {
      return FEE_TIER_LOW;
    } else if (totalPrice <= FEE_THRESHOLD_HIGH) {
      return FEE_TIER_MID;
    }
    return FEE_TIER_HIGH;
  };

  // Calculate fees
  const calculateFees = () => {
    const price = parseFloat(formData.price) || 0;
    const ticketCount = parseInt(formData.ticket_count) || 1;
    const fee = getFeeForPrice(price);
    
    let buyerPays, sellerReceives;
    
    if (formData.fee_payer === 'buyer') {
      buyerPays = price + fee;
      sellerReceives = price;
    } else if (formData.fee_payer === 'seller') {
      buyerPays = price;
      sellerReceives = price - fee;
    } else {
      buyerPays = price + (fee / 2);
      sellerReceives = price - (fee / 2);
    }
    
    return {
      fee: fee,
      totalAmount: price,
      ticketCount: ticketCount,
      buyerPays: buyerPays,
      sellerReceives: sellerReceives
    };
  };

  // Get fee tier explanation text
  const getFeeTierText = () => {
    const price = parseFloat(formData.price) || 0;
    if (price > 0) {
      if (price <= FEE_THRESHOLD_LOW) {
        return `Montant: ${price.toFixed(2)}$ → Frais: ${FEE_TIER_LOW.toFixed(2)}$`;
      } else if (price <= FEE_THRESHOLD_HIGH) {
        return `Montant: ${price.toFixed(2)}$ → Frais: ${FEE_TIER_MID.toFixed(2)}$`;
      } else {
        return `Montant: ${price.toFixed(2)}$ → Frais: ${FEE_TIER_HIGH.toFixed(2)}$`;
      }
    }
    return `≤125$ = ${FEE_TIER_LOW.toFixed(2)}$ | 125,01-250$ = ${FEE_TIER_MID.toFixed(2)}$ | >250$ = ${FEE_TIER_HIGH.toFixed(2)}$`;
  };

  const fees = calculateFees();

  // Validation
  const canProceedToStep2 = () => {
    return sellerInfo && buyerInfo && sellerClientNumber !== buyerClientNumber;
  };

  const canProceedToStep3 = () => {
    return formData.event_name && formData.event_date && formData.event_time && formData.venue && formData.venue.trim().length >= 2;
  };

  const canProceedToStep4 = () => {
    return formData.seat_type && formData.price && parseFloat(formData.price) > 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Prepare ticket details if siege_assigne
      let preparedTicketDetails = [];
      if (formData.seat_type === 'siege_assigne') {
        preparedTicketDetails = ticketDetails.map(t => ({
          level: t.level || null,
          section: t.section || null,
          row: t.row || null,
          seat: t.seat || null
        }));
      }

      const payload = {
        creator_role: formData.creator_role,
        buyer_client_number: buyerClientNumber,
        seller_client_number: sellerClientNumber,
        event_name: formData.event_name,
        event_date: formData.event_date,
        event_time: formData.event_time,
        venue: formData.venue,
        ticket_count: parseInt(formData.ticket_count),
        price: parseFloat(formData.price),
        currency: formData.currency,
        ticket_platform: formData.ticket_platform,
        event_type: formData.event_type,
        seat_type: formData.seat_type,
        special_section_details: formData.special_section_details,
        fee_payer: formData.fee_payer,
        ticket_details: preparedTicketDetails
      };

      await axios.post(`${API}/transactions`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Transfert créé avec succès!");
      onSuccess?.();
    } catch (error) {
      console.error("Error creating transaction:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setIsLoading(false);
    }
  };

  // Update ticket detail
  const updateTicketDetail = (index, field, value) => {
    setTicketDetails(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  return (
    <div className="space-y-6 relative">
      {/* Close button */}
      {onCancel && (
        <button
          onClick={onCancel}
          className="absolute -top-2 -right-2 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors z-10"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>
      )}
      
      {/* Confirmation View */}
      {showConfirmation ? (
        <div className="flex flex-col max-h-[60vh]">
          <div className="text-center pb-4 border-b flex-shrink-0">
            <div className="flex items-center justify-center gap-2 text-brand-700 text-xl font-semibold">
              <Ticket className="h-6 w-6" />
              Confirmez les informations
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-4 space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg">
              <div><span className="text-gray-500">Vendeur:</span> <strong>{sellerClientNumber}</strong> - {sellerInfo?.name}</div>
              <div><span className="text-gray-500">Acheteur:</span> <strong>{buyerClientNumber}</strong> - {buyerInfo?.name}</div>
              <div className="col-span-2"><span className="text-gray-500">Événement:</span> <strong>{formData.event_name}</strong></div>
              <div><span className="text-gray-500">Date:</span> <strong>{formData.event_date}</strong></div>
              <div><span className="text-gray-500">Heure:</span> <strong>{formData.event_time}</strong></div>
              {formData.venue && <div className="col-span-2"><span className="text-gray-500">Lieu:</span> <strong>{formData.venue}</strong></div>}
              <div><span className="text-gray-500">Billets:</span> <strong>{formData.ticket_count}</strong></div>
              <div><span className="text-gray-500">Prix:</span> <strong>{parseFloat(formData.price).toFixed(2)}$ {formData.currency}</strong></div>
              <div><span className="text-gray-500">Type:</span> <strong>{formData.seat_type === 'admission_generale' ? 'Admission générale' : formData.seat_type === 'siege_assigne' ? 'Siège assigné' : 'Zone spéciale'}</strong></div>
              <div><span className="text-gray-500">Plateforme:</span> <strong>{formData.ticket_platform}</strong></div>
            </div>

            {formData.seat_type === 'siege_assigne' && ticketDetails.length > 0 && (
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-gray-600 font-medium mb-2">Détails des billets:</p>
                {ticketDetails.map((ticket, idx) => (
                  <div key={idx} className="text-sm">
                    Billet {idx + 1}: {[
                      ticket.level && `Niveau ${ticket.level}`,
                      ticket.section && `Section ${ticket.section}`,
                      ticket.row && `Rangée ${ticket.row}`,
                      ticket.seat && `Siège ${ticket.seat}`
                    ].filter(Boolean).join(' / ') || 'Non spécifié'}
                  </div>
                ))}
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between"><span>Frais de transfert:</span><span>{fees.fee.toFixed(2)}$</span></div>
              <div className="flex justify-between text-green-700 font-semibold"><span>L'acheteur paie:</span><span>{fees.buyerPays.toFixed(2)}$</span></div>
              <div className="flex justify-between text-brand-700 font-semibold"><span>Le vendeur reçoit:</span><span>{fees.sellerReceives.toFixed(2)}$</span></div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t flex-shrink-0">
            <Button variant="outline" className="flex-1" onClick={() => setShowConfirmation(false)} disabled={isLoading}>
              Modifier
            </Button>
            <Button className="flex-1 bg-brand-600 hover:bg-brand-700" onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Création...</> : "Confirmer et créer"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Step 1: Client Selection */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <User className="h-5 w-5 text-brand-600" />
                Sélection des clients
              </h3>

              {/* Seller */}
              <div className="space-y-2">
                <Label>Numéro client du vendeur *</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ex: MM-001234"
                    value={sellerClientNumber}
                    onChange={(e) => setSellerClientNumber(e.target.value)}
                  />
                  <Button type="button" variant="outline" onClick={() => searchClient(sellerClientNumber, 'seller')} disabled={sellerSearching}>
                    {sellerSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
                {sellerInfo && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      <strong>{sellerInfo.name}</strong> - {sellerInfo.email}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Buyer */}
              <div className="space-y-2">
                <Label>Numéro client de l'acheteur *</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ex: MM-005678"
                    value={buyerClientNumber}
                    onChange={(e) => setBuyerClientNumber(e.target.value)}
                  />
                  <Button type="button" variant="outline" onClick={() => searchClient(buyerClientNumber, 'buyer')} disabled={buyerSearching}>
                    {buyerSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
                {buyerInfo && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      <strong>{buyerInfo.name}</strong> - {buyerInfo.email}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Invite other party note */}
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  <p className="mb-2">
                    Le client n'a pas de compte? Invitez-le à se créer un compte et à vous fournir son numéro de client.
                  </p>
                  <div className="flex items-center gap-2 bg-white rounded px-2 py-1.5 border border-blue-200">
                    <span className="text-xs font-mono flex-1 truncate">https://transfertdebillets.ca/auth</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                      onClick={() => {
                        navigator.clipboard.writeText('https://transfertdebillets.ca/auth');
                        toast.success('Lien copié!');
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>

              {sellerClientNumber && buyerClientNumber && sellerClientNumber === buyerClientNumber && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>Le vendeur et l'acheteur doivent être différents</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end pt-4">
                <Button onClick={() => setCurrentStep(2)} disabled={!canProceedToStep2()}>
                  Suivant <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Event Information */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-brand-600" />
                Informations sur l'événement
              </h3>

              <div className="space-y-2">
                <Label>Nom de l'événement *</Label>
                <Input
                  placeholder="Ex: Coldplay - Music of the Spheres Tour"
                  value={formData.event_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_name: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure *</Label>
                  <Input
                    type="time"
                    value={formData.event_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_time: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Lieu de l&apos;événement *</Label>
                <Input
                  placeholder="Ex: Centre Bell, Montréal"
                  value={formData.venue}
                  onChange={(e) => setFormData(prev => ({ ...prev, venue: e.target.value }))}
                  required
                />
                <p className="text-[11px] text-gray-500">
                  Requis même pour une admission générale.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Type d'événement</Label>
                <Select value={formData.event_type} onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="concert">Concert</SelectItem>
                    <SelectItem value="sport">Événement sportif</SelectItem>
                    <SelectItem value="theatre">Théâtre / Spectacle</SelectItem>
                    <SelectItem value="festival">Festival</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>Retour</Button>
                <Button onClick={() => setCurrentStep(3)} disabled={!canProceedToStep3()}>
                  Suivant <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Ticket Information */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Ticket className="h-5 w-5 text-brand-600" />
                Informations sur les billets
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de billets *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.ticket_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, ticket_count: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prix total *</Label>
                  <div className="flex gap-2">
                    <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                      <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CAD">CAD</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="150.00"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Type de billets *</Label>
                <RadioGroup value={formData.seat_type} onValueChange={(value) => setFormData(prev => ({ ...prev, seat_type: value }))}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="admission_generale" id="admin_ag" />
                    <Label htmlFor="admin_ag">Admission générale</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="siege_assigne" id="admin_sa" />
                    <Label htmlFor="admin_sa">Siège assigné</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="zone_speciale" id="admin_zs" />
                    <Label htmlFor="admin_zs">Zone spéciale (VIP, Parterre, etc.)</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.seat_type === 'zone_speciale' && (
                <div className="space-y-2">
                  <Label>Précisez la zone</Label>
                  <Input
                    placeholder="Ex: VIP, Parterre, Loge"
                    value={formData.special_section_details}
                    onChange={(e) => setFormData(prev => ({ ...prev, special_section_details: e.target.value }))}
                  />
                </div>
              )}

              {formData.seat_type === 'siege_assigne' && (
                <div className="space-y-3">
                  <Label>Détails des billets (Niveau / Section / Rangée / Siège)</Label>
                  {ticketDetails.map((ticket, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-2 p-2 bg-gray-50 rounded">
                      <Input placeholder="Niveau" value={ticket.level} onChange={(e) => updateTicketDetail(idx, 'level', e.target.value)} />
                      <Input placeholder="Section" value={ticket.section} onChange={(e) => updateTicketDetail(idx, 'section', e.target.value)} />
                      <Input placeholder="Rangée" value={ticket.row} onChange={(e) => updateTicketDetail(idx, 'row', e.target.value)} />
                      <Input placeholder="Siège" value={ticket.seat} onChange={(e) => updateTicketDetail(idx, 'seat', e.target.value)} />
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <Label>Plateforme de billetterie</Label>
                <Select value={formData.ticket_platform} onValueChange={(value) => setFormData(prev => ({ ...prev, ticket_platform: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {platforms.map(p => (
                      <SelectItem key={p.id || p.name} value={p.name}>{p.display_name || p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setCurrentStep(2)}>Retour</Button>
                <Button onClick={() => setCurrentStep(4)} disabled={!canProceedToStep4()}>
                  Suivant <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Fee & Confirmation */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-brand-600" />
                Frais et finalisation
              </h3>

              <div className="space-y-2">
                <Label>Qui paie les frais de transfert?</Label>
                <RadioGroup value={formData.fee_payer} onValueChange={(value) => setFormData(prev => ({ ...prev, fee_payer: value }))}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="buyer" id="admin_fee_buyer" />
                    <Label htmlFor="admin_fee_buyer">L'acheteur</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="seller" id="admin_fee_seller" />
                    <Label htmlFor="admin_fee_seller">Le vendeur</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="split" id="admin_fee_split" />
                    <Label htmlFor="admin_fee_split">Partagé 50/50</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="bg-brand-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Prix des billets:</span>
                  <span>{parseFloat(formData.price || 0).toFixed(2)}$ {formData.currency}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Frais de transfert:</span>
                  <span>{fees.fee.toFixed(2)}$</span>
                </div>
                <div className="text-xs text-gray-600 italic">
                  {getFeeTierText()}
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-semibold text-green-700">
                    <span>L'acheteur paie:</span>
                    <span>{fees.buyerPays.toFixed(2)}$</span>
                  </div>
                  <div className="flex justify-between font-semibold text-brand-700">
                    <span>Le vendeur reçoit:</span>
                    <span>{fees.sellerReceives.toFixed(2)}$</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes admin (optionnel)</Label>
                <Textarea
                  placeholder="Notes internes..."
                  value={formData.admin_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, admin_notes: e.target.value }))}
                  rows={2}
                />
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setCurrentStep(3)}>Retour</Button>
                <Button className="bg-brand-600 hover:bg-brand-700" onClick={() => setShowConfirmation(true)}>
                  Vérifier et confirmer
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Report bug link at the bottom */}
      <div className="flex justify-center pt-4 mt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={() => window.open('/contact', '_blank')}
          className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-brand-500 transition-colors"
        >
          <Bug className="h-3 w-3" />
          <span>Rapporter un problème</span>
        </button>
      </div>
    </div>
  );
};

export default AdminTransferForm;
